import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingCart, Menu, X, User, Sun, Moon, ChevronDown, Bell, MessageSquare, Truck, RefreshCw, Package, Tag, Info, Star, Percent, Clock, Gift, Shield, MapPin, Zap, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth, useCart, useTheme, useCurrency, useLanguage, useMarketplaceMode } from '../App';
import { CURRENCIES } from '../lib/currency';
import { LANGUAGES } from '../lib/i18n';
import SearchSuggestions from './SearchSuggestions';
import apiClient from '../lib/api';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const { theme, toggleTheme } = useTheme();
  const { currency, setCurrency, formatThreshold, showVAT, toggleVAT } = useCurrency();
  const { language, setLanguage, t } = useLanguage();
  const { isMultiVendor, loading: marketplaceModeLoading, announcementBar } = useMarketplaceMode();
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  // T8: compact mode for mobile scroll-hide
  const [isCompact, setIsCompact] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // T11: mobile search overlay
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const searchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const lastScrollYRef = useRef(0);

  // T8: Scroll handler — track direction, compact on scroll-down past 60px
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const previousY = lastScrollYRef.current;

      setIsScrolled(currentY > 10);

      // Only apply compact behaviour on mobile (<=768px)
      if (window.innerWidth <= 768) {
        if (currentY > 60 && currentY > previousY) {
          // Scrolling down past threshold
          setIsCompact(true);
        } else if (currentY < previousY) {
          // Scrolling up
          setIsCompact(false);
        }
      } else {
        // Always full on desktop
        setIsCompact(false);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch unread message count when user is logged in
  const unreadRef = useRef(true);
  useEffect(() => {
    unreadRef.current = true;
    if (!user) {
      setUnreadCount(0);
      return;
    }
    const fetchUnread = () => {
      if (!unreadRef.current) return;
      apiClient.get('/messages/unread-count')
        .then(function(res) {
          if (unreadRef.current) {
            setUnreadCount(res.data?.unread_count || 0);
          }
        })
        .catch(function() {
          // Non-critical — fail silently
        });
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => {
      unreadRef.current = false;
      clearInterval(interval);
    };
  }, [user]);

  // Fetch favorites count when user is logged in
  useEffect(() => {
    if (!user) {
      setFavoritesCount(0);
      return;
    }
    let active = true;
    const fetchFavorites = () => {
      apiClient.get('/favorites/')
        .then((res) => {
          if (active) {
            const data = res.data;
            setFavoritesCount(Array.isArray(data) ? data.length : data?.items?.length || 0);
          }
        })
        .catch(() => {});
    };
    fetchFavorites();
    // Listen for custom event to refresh count (fired when user adds/removes favorites)
    const handleFavChange = () => fetchFavorites();
    window.addEventListener('favorites-changed', handleFavChange);
    return () => {
      active = false;
      window.removeEventListener('favorites-changed', handleFavChange);
    };
  }, [user]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setMobileSearchOpen(false);
    }
  };

  const handleLogout = async () => {
    const currentPath = window.location.pathname;
    await logout(currentPath);
    // logout() handles redirect for protected routes; for public routes we stay in place
  };

  const handleSuggestionSelect = (term) => {
    const q = typeof term === 'string' ? term : (term?.query || term?.term || '');
    setSearchQuery(q);
    setShowSuggestions(false);
    setMobileSearchOpen(false);
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    }
  };

  // T11: When mobile search opens, focus the input
  useEffect(() => {
    if (mobileSearchOpen && mobileSearchInputRef.current) {
      setTimeout(() => mobileSearchInputRef.current?.focus(), 100);
    }
  }, [mobileSearchOpen]);

  const ANNOUNCEMENT_ICONS = {
    truck: Truck,
    refresh: RefreshCw,
    package: Package,
    tag: Tag,
    info: Info,
    star: Star,
    percent: Percent,
    clock: Clock,
    gift: Gift,
    shield: Shield,
    'map-pin': MapPin,
    zap: Zap,
    heart: Heart,
  };

  // Map well-known announcement-bar items to translation keys so the pills
  // ("We ship within 72 working hours" etc.) translate even though the text
  // is sourced from admin-edited DB content. If the admin has changed the
  // wording, fall back to the raw DB string.
  const ANNOUNCEMENT_TEXT_TO_KEY = {
    'We ship within 72 working hours': 'header.shipWithin',
    '14-day returns': 'header.returns14',
    'Delivery across the EU, UK & Norway': 'header.deliveryEU',
    'Delivery to All of EU': 'header.deliveryEU',  // legacy DB wording, still map it
  };
  const localizedAnnouncementText = (text) => {
    const key = ANNOUNCEMENT_TEXT_TO_KEY[text];
    if (!key) return text;
    const translated = t(key);
    return translated && translated !== key ? translated : text;
  };

  const enabledAnnouncementItems = (announcementBar?.items || []).filter(item => item.enabled);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-200 ${
        isScrolled
          ? 'bg-background/95 backdrop-blur-sm border-b shadow-sm'
          : 'bg-background border-b'
      }`}
      data-testid="header"
      style={{ willChange: 'transform' }}
    >
      <div className="container mx-auto px-4">
        {/* Top info bar — Desktop only, hidden on mobile when compact */}
        {announcementBar?.enabled !== false && enabledAnnouncementItems.length > 0 && (
          <div
            className="hidden lg:flex items-center justify-between py-2 text-sm border-b border-border/50 overflow-hidden"
            style={{
              transform: isCompact ? 'translateY(-100%)' : 'translateY(0)',
              maxHeight: isCompact ? '0' : '60px',
              opacity: isCompact ? 0 : 1,
              transition: 'transform 200ms ease, max-height 200ms ease, opacity 200ms ease',
              willChange: 'transform',
            }}
          >
            <div className="flex items-center gap-6 text-muted-foreground">
              {enabledAnnouncementItems.map((item, idx) => {
                const IconComponent = ANNOUNCEMENT_ICONS[item.icon];
                return (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span>•</span>}
                    <span className="flex items-center gap-1.5">
                      {IconComponent && <IconComponent className="h-3.5 w-3.5" />}
                      {localizedAnnouncementText(item.text)}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>
            <div className="flex items-center gap-4">
              {/* Currency Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors text-xs uppercase tracking-wide">
                    <span>{CURRENCIES[currency].symbol}</span>
                    <span>{currency}</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  {Object.entries(CURRENCIES).map(([code, curr]) => (
                    <DropdownMenuItem key={code} onClick={() => setCurrency(code)} className={code === currency ? 'bg-muted' : ''}>
                      <span className="font-mono text-sm w-8">{curr.symbol}</span>
                      <span className="text-sm">{code} — {curr.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors text-xs uppercase tracking-wide">
                    <span>{LANGUAGES[language]?.flag ?? LANGUAGES.en.flag}</span>
                    <span>{language.toUpperCase()}</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  {Object.entries(LANGUAGES).map(([code, lang]) => (
                    <DropdownMenuItem key={code} onClick={() => setLanguage(code)} className={code === language ? 'bg-muted' : ''}>
                      <span className="mr-2">{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* VAT Toggle — visible on every page so buyers can always switch */}
              <button
                onClick={toggleVAT}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showVAT ? 'Switch to prices excluding VAT' : 'Switch to prices including VAT'}
              >
                <span className="text-xs uppercase tracking-wide">
                  {showVAT ? t('header.inclVat') : t('header.exclVat')}
                </span>
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                <span className="text-xs uppercase tracking-wide">{t(theme === 'dark' ? 'header.light' : 'header.dark')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Main header row */}
        <div
          className="flex items-center justify-between gap-4"
          style={{
            height: isCompact ? '52px' : undefined,
            transition: 'height 200ms ease',
          }}
        >
          {/* Height on non-compact: use Tailwind classes, override with inline on compact */}
          <div className={`flex items-center justify-between w-full gap-4 ${isCompact ? '' : 'h-16 lg:h-20'}`}>
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center shrink-0"
              data-testid="logo-link"
            >
              <picture>
                <source
                  type="image/webp"
                  srcSet={theme === 'dark' ? '/logo-dark.webp' : '/logo.webp'}
                />
                <img
                  src={theme === 'dark' ? '/logo-dark.png' : '/logo.png'}
                  alt="ZIVU — Parts Delivered"
                  width="112"
                  height="56"
                  className="transition-all duration-200 object-contain"
                  style={{ height: isCompact ? '36px' : '56px', width: isCompact ? '72px' : '112px' }}
                />
              </picture>
            </Link>

            {/* Search bar — Desktop: always visible; Mobile: hidden (shown via search icon) */}
            <div className="hidden md:flex flex-1 max-w-xl mx-4 relative">
              <form
                onSubmit={handleSearch}
                className="w-full"
                data-testid="search-form"
              >
                <div className="relative w-full">
                  <Input
                    type="text"
                    placeholder={t('header.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    ref={searchInputRef}
                    className="h-12 pl-4 pr-12 font-mono text-sm border-2 border-input focus:border-accent rounded-none"
                    data-testid="search-input"
                    autoComplete="off"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    className="absolute right-0 top-0 h-12 w-12 bg-accent hover:bg-accent/90 rounded-none"
                    data-testid="search-submit"
                    aria-label="Search"
                  >
                    <Search className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </div>
              </form>
              {/* T11: SearchSuggestions — rendered outside the form, inside the relative parent */}
              <SearchSuggestions
                query={searchQuery}
                onSelect={handleSuggestionSelect}
                onClose={() => setShowSuggestions(false)}
                isOpen={showSuggestions && searchQuery.length > 0}
                inputRef={searchInputRef}
              />
            </div>

            {/* Desktop Nav Links */}
            <nav className="hidden lg:flex items-center gap-6">
              <Link
                to="/makes"
                className="font-heading text-sm uppercase tracking-wide hover:text-accent transition-colors"
              >
                {t('nav.makes') || 'Browse by Make'}
              </Link>
              <Link
                to="/search"
                className="font-heading text-sm uppercase tracking-wide hover:text-accent transition-colors"
              >
                {t('nav.allParts')}
              </Link>
              {/* Show "Sell on zivu.eu" only in multi-vendor mode, and only to non-sellers.
                  Guard against marketplaceModeLoading to prevent a flash of this link
                  while the /platform/mode fetch is in flight (default state is multi_vendor). */}
              {!marketplaceModeLoading && isMultiVendor && (!user || (user.role !== 'seller' && user.role !== 'admin' && user.role !== 'super_admin')) && (
                <Link
                  to="/register"
                  className="font-heading text-sm uppercase tracking-wide hover:text-accent transition-colors"
                >
                  Sell on zivu.eu
                </Link>
              )}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2 lg:gap-4">
              {/* T8/T11: Mobile search icon — shown when compact OR always on mobile */}
              <button
                onClick={() => setMobileSearchOpen(true)}
                className="md:hidden p-2 text-muted-foreground hover:text-foreground"
                aria-label="Search"
                data-testid="mobile-search-trigger"
              >
                <Search className="h-5 w-5" aria-hidden="true" />
              </button>

              {/* Mobile theme toggle — hidden when compact to save space */}
              <button
                onClick={toggleTheme}
                className={`lg:hidden p-2 text-muted-foreground hover:text-foreground ${isCompact ? 'hidden' : ''}`}
                data-testid="mobile-theme-toggle"
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
              </button>

              {/* Messages bell — shown when logged in */}
              {user && (
                <Link
                  to="/messages"
                  className="relative p-2 hover:bg-muted transition-colors"
                  aria-label={unreadCount > 0 ? `${unreadCount} unread messages` : 'Messages'}
                  data-testid="messages-link"
                >
                  <MessageSquare className="h-6 w-6" aria-hidden="true" />
                  {unreadCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 bg-accent text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full"
                      aria-hidden="true"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
              )}

              {/* Wishlist — shown when logged in */}
              {user && (
                <Link
                  to="/customer/favorites"
                  className="relative p-2 hover:bg-muted transition-colors"
                  aria-label={favoritesCount > 0 ? `${favoritesCount} saved items` : 'Wishlist'}
                  data-testid="wishlist-link"
                >
                  <Heart className="h-6 w-6" aria-hidden="true" />
                  {favoritesCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 bg-accent text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full"
                      aria-hidden="true"
                    >
                      {favoritesCount > 9 ? '9+' : favoritesCount}
                    </span>
                  )}
                </Link>
              )}

              {/* Cart */}
              <Link
                to="/cart"
                className="relative p-2 hover:bg-muted transition-colors"
                data-testid="cart-link"
                aria-label={`Shopping cart with ${cart.item_count} items`}
              >
                <ShoppingCart className="h-6 w-6" aria-hidden="true" />
                <AnimatePresence mode="wait">
                  {cart.item_count > 0 && (
                    <motion.span
                      key={cart.item_count}
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.5, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 25 }}
                      className="absolute -top-1 -right-1 bg-accent text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full"
                    >
                      {cart.item_count}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>

              {/* User menu - Desktop */}
              <div className="hidden lg:block">
                {user ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center gap-2 h-10"
                        data-testid="user-menu-trigger"
                      >
                        <User className="h-5 w-5" />
                        <span className="font-medium">{user.name.split(' ')[0]}</span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <Link to="/customer" data-testid="dashboard-link">{t('nav.myAccount')}</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/customer/orders" data-testid="orders-link">{t('nav.myOrders')}</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/customer/favorites" data-testid="favorites-link-dropdown" className="flex items-center gap-2">
                          <Heart className="h-4 w-4" aria-hidden="true" />
                          Favorites
                          {favoritesCount > 0 && (
                            <span className="ml-auto bg-accent text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">
                              {favoritesCount > 9 ? '9+' : favoritesCount}
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/customer/messages" data-testid="messages-link-dropdown" className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" aria-hidden="true" />
                          Messages
                          {unreadCount > 0 && (
                            <span className="ml-auto bg-accent text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">
                              {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                      {/* Seller Portal — only shown in multi-vendor mode.
                          Guard against marketplaceModeLoading to avoid a flash in single-vendor mode. */}
                      {!marketplaceModeLoading && isMultiVendor && (user.role === 'seller' || user.role === 'admin' || user.role === 'super_admin') && (
                        <DropdownMenuItem asChild>
                          <Link to="/seller" data-testid="seller-link">{t('nav.sellerPortal')}</Link>
                        </DropdownMenuItem>
                      )}
                      {(user.role === 'admin' || user.role === 'super_admin') && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin" data-testid="admin-link">{t('nav.adminPanel')}</Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                        {t('nav.logout')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      asChild
                      className="font-medium"
                      data-testid="login-btn"
                    >
                      <Link to="/login">{t('nav.login')}</Link>
                    </Button>
                    <Button
                      asChild
                      className="bg-accent hover:bg-accent/90 text-black font-bold uppercase tracking-wide rounded-sm"
                      data-testid="register-btn"
                    >
                      <Link to="/register">{t('nav.register')}</Link>
                    </Button>
                  </div>
                )}
              </div>

              {/* Mobile menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden"
                    data-testid="mobile-menu-trigger"
                    aria-label="Open mobile menu"
                  >
                    <Menu className="h-6 w-6" aria-hidden="true" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:w-80 p-0">
                  <div className="flex flex-col h-full">
                    {/* Mobile search */}
                    <form onSubmit={(e) => { handleSearch(e); setMobileMenuOpen(false); }} className="p-4 border-b">
                      <div className="relative">
                        <Input
                          type="text"
                          placeholder={t('header.searchMobile')}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="h-12 pl-4 pr-12 font-mono text-sm rounded-none"
                          data-testid="mobile-search-input"
                        />
                        <Button
                          type="submit"
                          size="icon"
                          className="absolute right-0 top-0 h-12 w-12 bg-accent rounded-none"
                          aria-label="Search"
                        >
                          <Search className="h-5 w-5" aria-hidden="true" />
                        </Button>
                      </div>
                    </form>

                    {/* Mobile nav links */}
                    <nav className="flex-1 p-4 space-y-1">
                      <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors">
                        {t('nav.home')}
                      </Link>
                      <Link to="/makes" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors">
                        {t('nav.makes') || 'Browse by Make'}
                      </Link>
                      <Link to="/search" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors">
                        {t('nav.allParts')}
                      </Link>
                      {/* Mobile: "Sell on zivu.eu" only in multi-vendor mode.
                          Guard against marketplaceModeLoading to prevent a flash in single-vendor mode. */}
                      {!marketplaceModeLoading && isMultiVendor && (!user || (user.role !== 'seller' && user.role !== 'admin' && user.role !== 'super_admin')) && (
                        <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors">
                          Sell on zivu.eu
                        </Link>
                      )}
                      <Link to="/cart" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors">
                        {t('nav.cart')} ({cart.item_count})
                      </Link>

                      {/* Mobile currency + language */}
                      <div className="py-2 flex gap-4 px-4">
                        <select value={currency} onChange={e => setCurrency(e.target.value)} className="bg-muted text-xs rounded px-2 py-1 border">
                          {Object.entries(CURRENCIES).map(([code, curr]) => (
                            <option key={code} value={code}>{curr.symbol} {code}</option>
                          ))}
                        </select>
                        <select value={language} onChange={e => setLanguage(e.target.value)} className="bg-muted text-xs rounded px-2 py-1 border">
                          {Object.entries(LANGUAGES).map(([code, lang]) => (
                            <option key={code} value={code}>{lang.flag} {lang.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="py-4">
                        <div className="border-t border-border" />
                      </div>

                      {user ? (
                        <>
                          <Link to="/customer" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors">
                            {t('nav.myAccount')}
                          </Link>
                          <Link to="/customer/orders" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors">
                            {t('nav.myOrders')}
                          </Link>
                          <Link to="/messages" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors flex items-center justify-between">
                            <span>Messages</span>
                            {unreadCount > 0 && (
                              <span className="bg-accent text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                {unreadCount > 9 ? '9+' : unreadCount}
                              </span>
                            )}
                          </Link>
                          <Link to="/customer/favorites" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors flex items-center justify-between">
                            <span>Wishlist</span>
                            {favoritesCount > 0 && (
                              <span className="bg-accent text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                {favoritesCount > 9 ? '9+' : favoritesCount}
                              </span>
                            )}
                          </Link>
                          {/* Mobile: Seller Portal only in multi-vendor mode.
                              Guard against marketplaceModeLoading to avoid a flash in single-vendor mode. */}
                          {!marketplaceModeLoading && isMultiVendor && (user.role === 'seller' || user.role === 'admin' || user.role === 'super_admin') && (
                            <Link to="/seller" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors">
                              {t('nav.sellerPortal')}
                            </Link>
                          )}
                          {(user.role === 'admin' || user.role === 'super_admin') && (
                            <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors">
                              {t('nav.adminPanel')}
                            </Link>
                          )}
                          <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full text-left py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors text-destructive">
                            {t('nav.logout')}
                          </button>
                        </>
                      ) : (
                        <>
                          <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors">
                            {t('nav.login')}
                          </Link>
                          <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 font-heading text-lg uppercase tracking-wide hover:bg-muted transition-colors bg-accent text-white">
                            {t('nav.register')}
                          </Link>
                        </>
                      )}
                    </nav>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      {/* T11: Mobile search overlay — full-screen search with suggestions */}
      {mobileSearchOpen && (
        <div
          className="fixed inset-0 z-[60] bg-background flex flex-col md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Search"
        >
          {/* Overlay header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b">
            <form
              onSubmit={handleSearch}
              className="flex-1 flex items-center gap-2"
            >
              <div className="relative flex-1">
                <Input
                  ref={mobileSearchInputRef}
                  type="text"
                  placeholder="Search for parts, makes, OEM codes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-11 pl-4 pr-10 font-mono text-sm border-2 border-accent rounded-none"
                  autoComplete="off"
                  autoFocus
                  data-testid="mobile-search-overlay-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                type="submit"
                size="icon"
                className="h-11 w-11 bg-accent hover:bg-accent/90 rounded-none shrink-0"
                aria-label="Search"
              >
                <Search className="h-5 w-5" aria-hidden="true" />
              </Button>
            </form>
            <button
              onClick={() => { setMobileSearchOpen(false); setSearchQuery(''); }}
              className="p-2 text-muted-foreground hover:text-foreground shrink-0"
              aria-label="Close search"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Suggestions — fills remaining height */}
          <div className="flex-1 overflow-y-auto">
            <SearchSuggestions
              query={searchQuery}
              onSelect={handleSuggestionSelect}
              onClose={() => setMobileSearchOpen(false)}
              isOpen={true}
              inputRef={mobileSearchInputRef}
              inlineMode={true}
            />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
